import { useEffect, useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { TimeRangePicker } from "./TimeRangePicker";
import {
  type TimeRangePickerDataProps,
  timeRangePickerDataSchema,
} from "./TimeRangePicker.schema";
import type { TimeRange } from "./TimeRangePicker.types";
import { TimeRangePickerAxis } from "./TimeRangePickerAxis";
import { TimeRangePickerOverview } from "./TimeRangePickerOverview";
import { TimeRangePickerTrack } from "./TimeRangePickerTrack";
import { TIME_RANGE_PICKER_TAG_NAME } from "./constants";

export interface TimeRangePickerHandle {
  value: (value: TimeRange[]) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TIME_RANGE_PICKER_TAG_NAME]: {
      props: TimeRangePickerDataProps;
      handle: TimeRangePickerHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-time-range-picker": HTMLElement & TimeRangePickerHandle;
  }
}

export function RWCTimeRangePicker({
  ref,
  label,
  value: initialValue,
  disabled: initialDisabled,
  readonly,
  overview,
  ...props
}: TimeRangePickerDataProps & { ref?: Ref<TimeRangePickerHandle> }) {
  const host = useIslandHost();

  const [value, setValue] = useState<TimeRange[]>(initialValue);
  const [disabled, setDisabled] = useState(initialDisabled);

  useImperativeHandle(
    ref,
    () => ({
      value: setValue,
      disabled: (nextDisabled = true) => setDisabled(nextDisabled),
    }),
    [],
  );

  useEffect(() => {
    getElementInternals(host)?.setFormValue(
      disabled ? null : JSON.stringify(value),
    );
  }, [host, value, disabled]);

  return (
    <TimeRangePicker
      {...props}
      value={value}
      onValueChange={setValue}
      disabled={disabled}
      readOnly={readonly}
    >
      <TimeRangePickerTrack aria-label={label} />
      <TimeRangePickerAxis />
      {overview ? <TimeRangePickerOverview /> : null}
    </TimeRangePicker>
  );
}

componentManager.register(
  rwc(TIME_RANGE_PICKER_TAG_NAME, {
    component: RWCTimeRangePicker,
    dataSchema: timeRangePickerDataSchema,
    formAssociated: true,
  }),
);
